import { Campaign, CampaignWrite } from "src/api/client-api/useCampaign";

export type CampaignSortKey = "label" | "year" | "startDate" | "endDate";
export type CampaignSortDirection = "asc" | "desc";
export type CampaignSort = {
    key: CampaignSortKey;
    direction: CampaignSortDirection;
};

export type CampaignDraft = {
    id?: number;
    label: string;
    year: string;
    startDate: string;
    endDate: string;
};

export type CampaignDraftField = "label" | "year" | "startDate" | "endDate";
export type CampaignDraftError = "required" | "invalidYear" | "endBeforeStart";
export type CampaignDraftErrors = Partial<Record<CampaignDraftField, CampaignDraftError>>;

const toDateInputValue = (isoDate: string): string => isoDate.slice(0, 10);

export const emptyCampaignDraft = (): CampaignDraft => ({
    label: "",
    year: "",
    startDate: "",
    endDate: "",
});

export const toCampaignDraft = (campaign: Campaign): CampaignDraft => ({
    id: campaign.id,
    label: campaign.label,
    year: String(campaign.year),
    startDate: toDateInputValue(campaign.startDate),
    endDate: toDateInputValue(campaign.endDate),
});

const mostRelevant = (campaigns: Campaign[], currentYear: number): Campaign | undefined => {
    const completed = campaigns.filter((campaign) => campaign.year < currentYear);
    const candidates = completed.length > 0 ? completed : campaigns;

    if (candidates.length === 0) {
        return undefined;
    }

    return candidates.reduce((best, campaign) => (campaign.year > best.year ? campaign : best));
};

export const resolveDefaultCampaign = (
    campaigns: Campaign[],
    currentYear: number = new Date().getFullYear(),
): Campaign | undefined =>
    mostRelevant(
        campaigns.filter((campaign) => campaign.active),
        currentYear,
    ) ?? mostRelevant(campaigns, currentYear);

export const toCampaignUpdate = (draft: CampaignDraft): Omit<CampaignWrite, "active"> & { id: number } => {
    if (draft.id === undefined) {
        throw new Error("A campaign update needs the campaign id");
    }

    return {
        id: draft.id,
        label: draft.label.trim(),
        year: Number(draft.year),
        startDate: draft.startDate,
        endDate: draft.endDate,
    };
};

export const sortCampaigns = (campaigns: Campaign[], sort: CampaignSort): Campaign[] => {
    const direction = sort.direction === "asc" ? 1 : -1;

    return [...campaigns].sort((left, right) => {
        if (sort.key === "year") {
            return direction * (left.year - right.year);
        }

        return direction * left[sort.key].localeCompare(right[sort.key]);
    });
};

export const validateCampaignDraft = (draft: CampaignDraft): CampaignDraftErrors => {
    const errors: CampaignDraftErrors = {};

    if (draft.label.trim() === "") {
        errors.label = "required";
    }

    if (draft.year === "") {
        errors.year = "required";
    } else if (/^\d{4}$/.test(draft.year) === false) {
        errors.year = "invalidYear";
    }

    if (draft.startDate === "") {
        errors.startDate = "required";
    }

    if (draft.endDate === "") {
        errors.endDate = "required";
    } else if (draft.startDate !== "" && draft.endDate <= draft.startDate) {
        errors.endDate = "endBeforeStart";
    }

    return errors;
};

export const toCampaignWrite = (draft: CampaignDraft, active: boolean): CampaignWrite => ({
    id: draft.id,
    label: draft.label.trim(),
    year: Number(draft.year),
    startDate: draft.startDate,
    endDate: draft.endDate,
    active,
});
